import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  ArrowLeftIcon,
  CloudArrowUpIcon,
  TrashIcon,
  DocumentTextIcon,
  ArrowUpTrayIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { useNavigate, useParams } from "react-router-dom";
import {
  getUploadedProof,
  uploadDeliveryProof,
  deleteUploadedProof,
} from "@/store/slices/newDeliverySlice";
import ShowLoader from "@/components/Loader";
import DocumentViewModal from "@/components/DocumentViewModal";
import Toaster from "@/components/Toaster";
import { useTranslation } from "react-i18next";

export default function ProofUpload() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Use delivery.json
  const { t } = useTranslation("delivery");

  const { uploaded_proofes, loading } = useSelector(
    (state) => state.delivery
  );

  const MAX_IMAGES = 10;
  const [images, setImages] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [isBootLoading, setIsBootLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpenReceipt, setIsOpenReceipt] = useState("");

  // Modal & Remark States
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(""); // 'draft' or 'confirm'
  const [remark, setRemark] = useState("");
  const [remarkError, setRemarkError] = useState("");

  useEffect(() => {
    if (!id) return;

    setIsBootLoading(true);

    dispatch(getUploadedProof({ id }))
      .unwrap()
      .finally(() => setIsBootLoading(false));
  }, [id, dispatch]);

  // Auto-fill previously uploaded proofs (draft/confirm) into local UI state
  useEffect(() => {
    if (!Array.isArray(uploaded_proofes)) return;

    const imgs = uploaded_proofes
      .filter((p) => p?.type === "image" && p?.file)
      .map((p) => ({
        id: p.id,
        proof_id: p.id,
        file: p.file,
        preview: p.file,
        timestamp: p?.created_at || p?.updated_at || "",
        status: p?.status,
        isExisting: true,
      }))
      .slice(0, MAX_IMAGES);

    const recs = uploaded_proofes
      .filter((p) => p?.type === "receipt" && p?.file)
      .map((p) => {
        const nameFromUrl = (() => {
          try {
            const u = new URL(p.file);
            const last = u.pathname.split("/").pop();
            return decodeURIComponent(last || "Receipt");
          } catch {
            const last = String(p.file).split("/").pop();
            return last || "Receipt";
          }
        })();

        return {
          id: p.id,
          proof_id: p.id,
          file: p.file,
          name: nameFromUrl,
          status: p?.status,
          isExisting: true,
        };
      });

    setImages(imgs);
    setReceipts(recs);
  }, [uploaded_proofes]);

  /* ================= HANDLERS ================= */

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);

    const allowedImageTypes = [
      "image/png",
      "image/jpeg",
      "image/webp",
    ];

    const invalid = files.filter(
      (f) => !allowedImageTypes.includes(f.type)
    );

    if (invalid.length) {
      toast.error(t("images_allowed_only"));
      e.target.value = "";
      return;
    }

    if (images.length + files.length > MAX_IMAGES) {
      toast.warn(t("maximum_images_allowed", { count: MAX_IMAGES }));
      e.target.value = "";
      return;
    }

    const newImages = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      timestamp: new Date().toLocaleString(),
    }));

    setImages((prev) => [...prev, ...newImages]);
    e.target.value = "";
  };

  const handleReceiptUpload = (e) => {
    const files = Array.from(e.target.files);

    const allowedReceiptTypes = [
      "application/pdf",
      "image/png",
      "image/jpeg",
      "image/webp",
    ];

    const invalid = files.filter(
      (f) => !allowedReceiptTypes.includes(f.type)
    );

    if (invalid.length) {
      toast.error(t("receipts_allowed_only"));
      e.target.value = "";
      return;
    }

    const newReceipts = files.map((file) => ({
      file,
      name: file.name,
    }));

    setReceipts((prev) => [...prev, ...newReceipts]);
    e.target.value = "";
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const removeReceipt = (index) => {
    setReceipts((prev) => prev.filter((_, i) => i !== index));
  };

  /* ================= API ACTIONS ================= */

  const openRemarkPopup = (status) => {
    if (images.length === 0 && receipts.length === 0) {
      toast.error(t("please_upload_at_least_one_image_or_receipt"));
      return;
    }

    setRemarkError("");
    setCurrentStatus(status);
    setShowRemarkModal(true);
  };

  const finalSubmit = () => {
    const r = String(remark || "").trim();

    if (r.length < 5) {
      setRemarkError(t("remark_minimum_characters"));
      return;
    }

    if (r.length > 50) {
      setRemarkError(t("remark_maximum_characters"));
      return;
    }

    const payload = {
      id: id,
      remark: r,
      status: currentStatus,
      images: images,
      receipts: receipts,
    };

    setIsSubmitting(true);

    dispatch(uploadDeliveryProof(payload))
      .unwrap()
      .then((res) => {
        if (res?.status) {
          Toaster.success(
            res.message || t("delivery_proof_upload_successfully")
          );

          setShowRemarkModal(false);
          setRemarkError("");

          if (currentStatus === "confirm") {
            navigate(-1);
          }
        } else {
          Toaster.error(
            res?.message || t("failed_to_upload_proofs")
          );
        }
      })
      .catch((err) => {
        Toaster.error(
          err.message || t("failed_to_upload_proofs")
        );
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F4F4F2]">
      {isSubmitting && <ShowLoader />}

      {/* HEADER */}
      <header className="bg-[#065C36] text-white shrink-0">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 py-5 sm:py-6 flex flex-col gap-4">

          {/* Back Button */}
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-xs sm:text-sm hover:opacity-90"
          >
            <ArrowLeftIcon className="w-4 h-4" />

            <span className="hidden sm:inline">
              {t("back_to_in_progress_departed_drop_off_order_details")}
            </span>

            <span className="sm:hidden">
              {t("back")}
            </span>
          </button>

          {/* Title */}
          <h1 className="text-center text-lg sm:text-xl font-semibold">
            {t("proof_upload")}
          </h1>
        </div>
      </header>

      {/* CONTENT */}
      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-10 flex-1 space-y-8 sm:space-y-10 text-gray-800 min-w-0">

        {isBootLoading && (
          <div className="text-center text-sm text-gray-500">
            {t("loading_uploaded_proofs")}
          </div>
        )}

        {/* IMAGE SECTION */}
        <div>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4">

            <h2 className="flex items-center gap-2 font-medium text-sm sm:text-base">
              <ArrowUpTrayIcon className="w-5 h-5 shrink-0" />
              {t("delivery_proof_images_upload")}
            </h2>

            <span className="text-xs text-gray-500 bg-gray-200 px-3 py-1 rounded-full w-fit">
              {t("uploaded_images_count", {
                count: images.length,
                max: MAX_IMAGES,
              })}
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
            {images.map((img, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden relative"
              >
                <img
                  src={img.preview}
                  alt={t("preview")}
                  className="h-40 w-full object-cover"
                />

                <button
                  onClick={() => {
                    removeImage(index);

                    if (img?.isExisting) {
                      dispatch(
                        deleteUploadedProof({
                          id: img?.id,
                        })
                      );
                    }
                  }}
                  className="absolute top-2 right-2 bg-white p-1 rounded-full shadow hover:bg-red-50"
                >
                  <TrashIcon className="w-4 h-4 text-red-500" />
                </button>

                <div className="text-[10px] text-gray-500 p-2 font-medium">
                  {img.timestamp}
                </div>
              </div>
            ))}

            {images.length < MAX_IMAGES && (
              <label className="border-2 border-dashed border-blue-400 bg-white rounded-xl h-48 flex flex-col items-center justify-center cursor-pointer text-gray-400 hover:bg-blue-50 transition-colors">
                <CloudArrowUpIcon className="w-8 h-8 mb-2 text-gray-300" />

                <span className="text-sm">
                  {t("add_image")}
                </span>

                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                  multiple
                  hidden
                  onChange={handleImageUpload}
                />
              </label>
            )}
          </div>
        </div>

        {/* RECEIPT SECTION */}
        <div>
          <h2 className="flex items-center gap-2 font-medium mb-4">
            <ArrowUpTrayIcon className="w-5 h-5" />
            {t("upload_pickup_receipts")}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {receipts.map((receipt, index) => (
              <div
                key={index}
                className="flex justify-between items-center bg-[#FFF1F1] p-3 rounded-lg border border-[#FFDEDE]"
              >
                <div className="flex items-center gap-2 text-[12px] font-medium text-gray-700 truncate">
                  <DocumentTextIcon className="w-6 h-6 text-gray-600" />

                  <span className="truncate">
                    {receipt.name}
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <EyeIcon
                    className="w-4 h-4 text-[#F4B11F] cursor-pointer"
                    onClick={() =>
                      setIsOpenReceipt(receipt?.file)
                    }
                  />

                  <TrashIcon
                    className="w-4 h-4 text-red-500 cursor-pointer"
                    onClick={() => {
                      removeReceipt(index);

                      if (receipt?.isExisting) {
                        dispatch(
                          deleteUploadedProof({
                            id: receipt?.id,
                          })
                        );
                      }
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          <label className="inline-block mt-6 cursor-pointer">
            <span className="flex items-center gap-2 px-4 py-2 border-2 border-[#F4B11F] text-[#F4B11F] rounded-lg text-xs font-bold hover:bg-[#F4B11F] hover:text-white transition">
              <ArrowUpTrayIcon className="w-4 h-4 stroke-[3px]" />
              {t("add_receipt")}
            </span>

            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/png,image/jpeg,image/webp"
              multiple
              hidden
              onChange={handleReceiptUpload}
            />
          </label>
        </div>
      </div>

      {/* FOOTER ACTIONS */}
      <div className="bg-[#065C36] px-4 sm:px-6 py-4 sm:py-6 sticky bottom-0 pb-[max(1rem,env(safe-area-inset-bottom,0px))]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row gap-3 sm:gap-6">

          {/* <button
            type="button"
            onClick={() => openRemarkPopup("draft")}
            className="flex-1 border-2 border-white text-white py-3.5 rounded-xl font-bold hover:bg-white hover:text-[#065C36] transition"
          >
            {t("save_as_draft")}
          </button> */}

          <button
            type="button"
            onClick={() => openRemarkPopup("confirm")}
            className="w-[600px] mx-auto bg-[#F4B11F] text-white py-3.5 rounded-xl font-bold shadow-lg hover:bg-[#d99a1a] transition"
          >
            {t("submit")}
          </button>
        </div>
      </div>

      {/* REMARK POPUP MODAL */}
      {showRemarkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">

          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl overflow-hidden">
            <img
              src="/img/logos/logo.png"
              alt={t("logo")}
              className="absolute inset-0 h-full w-full object-contain opacity-5 pointer-events-none"
            />

            <div className="relative z-10">
              <h3 className="mb-4 text-center text-xl font-bold text-gray-800">
                {t("enter_your_remarks")}
              </h3>

              <textarea
                className="w-full rounded-xl border border-gray-200 p-4 text-sm outline-none focus:border-[#F4B11F] focus:ring-1 focus:ring-[#F4B11F]"
                rows="4"
                placeholder={t("type_your_remarks_here")}
                value={remark}
                maxLength={50}
                onChange={(e) => {
                  setRemark(e.target.value);

                  if (remarkError) {
                    setRemarkError("");
                  }
                }}
              />

              <div className="mt-1 flex items-start justify-between gap-3">
                {remarkError ? (
                  <p className="text-xs text-red-600">
                    {remarkError}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500">
                    {t("remark_character_limit")}
                  </p>
                )}

                <p className="text-xs text-gray-400 shrink-0">
                  {String(remark || "").trim().length}/50
                </p>
              </div>

              <div className="mt-6 flex gap-4">
                <button
                  onClick={() => {
                    setShowRemarkModal(false);
                    setRemark("");
                    setRemarkError("");
                  }}
                  className="flex-1 rounded-xl py-3 text-lg font-semibold text-[#F4B11F]"
                >
                  {t("back")}
                </button>

                <button
                  disabled={loading}
                  onClick={finalSubmit}
                  className="flex-1 rounded-xl bg-[#F4B11F] py-3 text-lg font-bold text-white shadow-md transition hover:bg-[#d99a1a] disabled:opacity-50"
                >
                  {loading
                    ? t("processing")
                    : t("submit")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isOpenReceipt && (
        <DocumentViewModal
          documentUrl={isOpenReceipt}
          setIsDocumentViewModalOpen={setIsOpenReceipt}
        />
      )}
    </div>
  );
}